import React, { useCallback, useEffect, useState } from 'react';
import SpecsNote from '../components/common/SpecsNote';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { ChevronLeft, Check, Ruler, FileText } from 'lucide-react';
import {
    FrameIcon, ForkIcon, ShockIcon, MotorIcon, BatteryIcon, ChargerIcon, DisplayIcon, BrakeIcon, RotorIcon,
    DerailleurIcon, ShifterIcon, CassetteIcon, ChainIcon, CrankIcon, WheelIcon, TireIcon,
    HandlebarIcon, StemIcon, SaddleIcon, SeatpostIcon, PedalIcon, WeightIcon, MaxWeightIcon
} from '../components/common/BikeSpecIcons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { Helmet } from 'react-helmet-async';
import 'swiper/css';
import 'swiper/css/pagination';
import { slugify, formatPrice } from '../utils';

import { useCart } from '../context/CartContext';
import seo from '../seo/seoConfig.json';
import { bikeTypeTitle, bikeTitle, describeBike, describeAccessory } from '../seo/productText';
import { findGeometry, kgToLb } from '../data/geometry';
import maxWeight from '../data/maxWeight.json';
import SizeGeometryModal from '../components/product/SizeGeometryModal';

const cleanImageUrl = (url) => {
    if (!url) return url;
    if (typeof url === 'string' && url.includes('raymon-bicycles.com/_next/image')) {
        const match = url.match(/url=([^&]+)/);
        if (match) {
            const decoded = decodeURIComponent(match[1]);
            return decoded.startsWith('http') ? decoded : `https://b2b.raymon-bicycles.com${decoded}`;
        }
    }
    return url;
};

const ProductDetail = () => {
    const { id } = useParams();
    const { addToCart } = useCart();

    // State Declarations
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSize, setSelectedSize] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [activeImage, setActiveImage] = useState(null);
    const [activeColorIndex, setActiveColorIndex] = useState(0);
    const [selectedFrame, setSelectedFrame] = useState(null);
    const [serie, setSerie] = useState(null);
    const [specView, setSpecView] = useState(null);
    const closeSpecView = useCallback(() => setSpecView(null), []);

    // Filter images based on selected color or frame
    const getDisplayedImages = () => {
        if (!product || !product.images || product.images.length === 0) return [];

        const hasFrames = product.tipos_marco && product.tipos_marco.length > 0;
        const hasColors = product.colores && product.colores.length > 0;

        // Synchronized formula based on user mapping (sets of 3)
        // Formula: (activeColorIndex * totalFrames + frameIndex) * 3
        if (hasFrames && hasColors) {
            const frameIndex = product.tipos_marco.indexOf(selectedFrame);
            const numFrames = product.tipos_marco.length;
            const start = (activeColorIndex * numFrames + (frameIndex >= 0 ? frameIndex : 0)) * 3;
            return product.images.slice(start, start + 3);
        }

        // Single factor filtering
        if (hasFrames) {
            const frameIndex = product.tipos_marco.indexOf(selectedFrame);
            const start = (frameIndex >= 0 ? frameIndex : 0) * 3;
            return product.images.slice(start, start + 3);
        }

        if (hasColors) {
            const start = activeColorIndex * 3;
            return product.images.slice(start, start + 3);
        }

        return product.images;
    };

    const displayedImages = getDisplayedImages();

    useEffect(() => {
        if (displayedImages.length > 0) {
            setActiveImage(displayedImages[0]);
        }
    }, [activeColorIndex, selectedFrame, product]);

    useEffect(() => {
        fetchProduct();
    }, [id]);

    const fetchProduct = async () => {
        try {
            setLoading(true);

            const isId = !isNaN(id);
            let matchedData = null;
            let matchedTable = '';

            const parseArray = (val) => {
                if (!val) return [];
                if (Array.isArray(val)) return val;
                if (typeof val === 'string') {
                    try {
                        const parsed = JSON.parse(val);
                        if (Array.isArray(parsed)) return parsed;
                    } catch (e) {}
                    return val.split(',').map((s) => s.trim()).filter(Boolean);
                }
                return [];
            };

            // 1. Try finding in 'bicicletas'
            let queryBikes = supabase.from('bicicletas').select('*');
            if (isId) {
                queryBikes = queryBikes.eq('id', id);
            } else {
                const nameSearch = id.replace(/-/g, ' ');
                queryBikes = queryBikes.ilike('modelo', nameSearch);
            }

            let { data: bikeData } = await queryBikes.maybeSingle();

            if (!bikeData && !isId) {
                const { data: models } = await supabase.from('bicicletas').select('id, modelo');
                const match = (models || []).find((b) => slugify(b.modelo) === id);
                if (match) ({ data: bikeData } = await supabase.from('bicicletas').select('*').eq('id', match.id).maybeSingle());
            }

            if (bikeData) {
                matchedData = bikeData;
                matchedTable = 'bicicletas';
            } else {
                // 2. Try finding in 'products'
                let queryProds = supabase.from('products').select('*');
                if (isId) {
                    queryProds = queryProds.eq('id', id);
                } else {
                    const nameSearch = id.replace(/-/g, ' ');
                    queryProds = queryProds.ilike('name', nameSearch);
                }
                let { data: prodData } = await queryProds.maybeSingle();

                // Names with accents or symbols ('Automáticos') lose them in the URL, so the lookup
                // above misses them: compare the slug of every name instead
                if (!prodData && !isId) {
                    const { data: names } = await supabase.from('products').select('id, name');
                    const match = (names || []).find((p) => slugify(p.name) === id);
                    if (match) ({ data: prodData } = await supabase.from('products').select('*').eq('id', match.id).maybeSingle());
                }

                if (prodData) {
                    matchedData = prodData;
                    matchedTable = 'products';
                }
            }

            if (!matchedData) {
                setProduct(null);
            } else {
                const mappedProduct = matchedTable === 'bicicletas' ? {
                    ...matchedData,
                    name: matchedData.modelo,
                    price: matchedData.precio_eur,
                    description: matchedData.description,
                    images: parseArray(matchedData.imagenes_urls).map(cleanImageUrl),
                    image_url: cleanImageUrl(parseArray(matchedData.imagenes_urls)[0]) || null,
                    tipos_marco: parseArray(matchedData.tipos_marco),
                    colores: parseArray(matchedData.colores),
                    max_weight_kg: maxWeight.kg[(matchedData.modelo || '').trim()] || null,
                    sizes: (() => {
                        const dbSizes = parseArray(matchedData.sizes);
                        const dbPrices = parseArray(matchedData.precio_eur);
                        return dbSizes.map((s, i) => ({
                            size: typeof s === 'object' && s ? (s.size || s.talla || '') : (s || ''),
                            price: dbPrices[i] || (Array.isArray(matchedData.precio_eur) ? matchedData.precio_eur[0] : matchedData.precio_eur)
                        }));
                    })(),
                    type: 'bikes'
                } : {
                    ...matchedData,
                    name: matchedData.name,
                    price: matchedData.price,
                    description: matchedData.description,
                    images: matchedData.image_url ? [cleanImageUrl(matchedData.image_url)] : [],
                    image_url: cleanImageUrl(matchedData.image_url),
                    sizes: parseArray(matchedData.sizes).map(s => {
                        if (typeof s === 'string') return { size: s, price: matchedData.price };
                        if (s && typeof s === 'object') {
                            return {
                                size: s.size || s.talla || '',
                                price: s.price || s.precio || matchedData.price
                            };
                        }
                        return { size: String(s || ''), price: matchedData.price };
                    }),
                    type: 'accessories'
                };

                setProduct(mappedProduct);

                // Auto-select first size if available
                if (mappedProduct.sizes?.length > 0) {
                    setSelectedSize(mappedProduct.sizes[0].size);
                }

                if (mappedProduct.tipos_marco?.length > 0) {
                    setSelectedFrame(mappedProduct.tipos_marco[0]);
                }

                if (mappedProduct.type === 'bikes' && mappedProduct.serie_id) {
                    // Series and category for the "Bicicletas / E-MTB / Serie X" line
                    const { data: serieData } = await supabase
                        .from('series')
                        .select('nombre, categoria_id')
                        .eq('id', mappedProduct.serie_id)
                        .maybeSingle();
                    setSerie(serieData || null);
                } else {
                    setSerie(null);
                }

                if (mappedProduct.serie_id) {
                    const { data: related } = await supabase
                        .from('bicicletas')
                        .select('id, modelo, precio_eur, imagenes_urls')
                        .eq('serie_id', mappedProduct.serie_id)
                        .neq('id', matchedData.id)
                        .limit(3);
                    if (related) {
                        setRelatedProducts(related.map(r => ({
                            ...r,
                            imagenes_urls: (r.imagenes_urls || []).map(cleanImageUrl)
                        })));
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching product:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div></div>;
    if (!product) return (
        <div className="min-h-screen bg-white flex items-center justify-center">
            <Helmet>
                <title>Producto no encontrado | PROOMTB</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            Producto no encontrado
        </div>
    );

    const mainName = product.name;
    const category = serie ? seo.categories[String(serie.categoria_id)] : null;
    // Size guide and geometry come from the Raymon 2026 catalog; bikes without data don't show the buttons
    const geometry = product.type === 'bikes' && serie ? findGeometry(serie.nombre, product.name) : null;
    const subName = '';
    // 'bicicleta eléctrica de montaña': same wording as the static title and description
    const typeTitle = product.type === 'bikes' ? bikeTypeTitle(serie?.categoria_id) : null;

    return (
        <div className="bg-white min-h-screen pt-24 pb-16">
            {/* Description, canonical, Open Graph and structured data are written into this
                page's static HTML at build time (scripts/inject-metadata.js), which is what
                Google and WhatsApp read. Repeating them here would create duplicate tags. */}
            <Helmet>
                <title>{product.type === 'bikes' ? bikeTitle(mainName, typeTitle) : `${mainName} | PROOMTB & ROAD`}</title>
            </Helmet>

            <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">

                {/* Breadcrumb / Back */}
                <div className="mb-8">
                    <Link to="/accesorios" className="inline-flex items-center text-sm font-bold uppercase tracking-wider text-black hover:text-gray-600 transition-colors">
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Back to Collection
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">

                    {/* Left Column: Image Gallery */}
                    <div className="lg:col-span-7 relative">
                        {/* Mobile Image Carousel */}
                        <div className="md:hidden w-full h-[400px] mb-8 bg-gray-50 rounded-3xl overflow-hidden relative">
                            <Swiper
                                modules={[Pagination]}
                                pagination={{ clickable: true }}
                                spaceBetween={20}
                                slidesPerView={1}
                                className="w-full h-full"
                            >
                                {displayedImages.map((img, idx) => (
                                    <SwiperSlide key={idx} className="flex items-center justify-center p-8">
                                        <img
                                            src={img}
                                            alt={`${product?.name} view ${idx + 1}`}
                                            className="w-full h-full object-contain mix-blend-multiply"
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>

                        {/* Desktop Image Gallery */}
                        <div className="hidden md:flex bg-white rounded-3xl p-8 lg:p-12 shadow-sm border border-gray-100 h-[600px]">
                            {/* Thumbnails */}
                            <div className="hidden md:flex flex-col space-y-4 mr-8 overflow-y-auto max-h-full scrollbar-hide py-4">
                                {displayedImages.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImage(img)}
                                        className={`w-16 h-16 rounded-xl border-2 overflow-hidden flex-shrink-0 transition-all ${activeImage === img ? 'border-black opacity-100' : 'border-gray-100 opacity-60 hover:opacity-100 hover:border-gray-300'}`}
                                    >
                                        <img src={img} alt={`Thumbnail ${idx}`} className="w-full h-full object-contain p-1" loading="lazy" />
                                    </button>
                                ))}
                            </div>

                            {/* Main Image */}
                            <div className="flex-1 flex items-center justify-center relative">
                                <img
                                    src={activeImage || (product?.image_url)}
                                    alt={product?.name}
                                    className="max-w-full max-h-full object-contain mix-blend-multiply transition-opacity duration-300"
                                    loading="eager"
                                    fetchPriority="high"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Details */}
                    <div className="lg:col-span-5 pt-4">
                        {/* Category / Breadcrumbs small */}
                        <div className="text-xs text-gray-400 mb-4 uppercase tracking-widest font-medium">
                            {product.type === 'bikes' ? (
                                <>
                                    <Link to="/catalogo" className="hover:text-black transition-colors">Bicicletas</Link>
                                    {category && <> / <Link to={`/category/${category.slug}`} className="hover:text-black transition-colors">{category.name}</Link></>}
                                    {serie?.nombre && <> / <Link to={`/series/${slugify(serie.nombre)}`} className="hover:text-black transition-colors">Serie {serie.nombre}</Link></>}
                                </>
                            ) : (
                                <>
                                    <Link to="/accesorios" className="hover:text-black transition-colors">Accesorios</Link>
                                    {product.category && <> / {product.category}</>}
                                </>
                            )}
                        </div>

                        {/* Title */}
                        <h1 className="text-5xl font-black italic uppercase tracking-tight mb-8 text-black leading-none">
                            {mainName} <span className="text-gray-300">{subName}</span>
                            {typeTitle && (
                                <span className="block mt-3 text-lg md:text-xl font-bold normal-case tracking-normal text-gray-500">
                                    {typeTitle.charAt(0).toUpperCase() + typeTitle.slice(1)} Raymon
                                </span>
                            )}
                        </h1>

                        {/* Description */}
                        <p className="text-gray-600 text-lg leading-relaxed mb-8">
                            {product.description || (product.type === 'bikes'
                                ? describeBike(product, typeTitle)
                                : describeAccessory(product))}
                        </p>

                        {/* Tech Specs Mock */}
                        <div className="flex items-center space-x-6 mb-12 text-sm text-gray-500 font-medium">
                            <div className="flex items-center"><span className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center mr-2 text-xs">R</span> Original</div>
                            <div className="flex items-center"><Check className="w-4 h-4 mr-2" /> Stock disponible</div>
                        </div>

                        {/* Frame Type Selection */}
                        {product?.tipos_marco && product.tipos_marco.length > 0 && (
                            <div className="mb-12">
                                <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Frame Type</h3>
                                <div className="flex flex-wrap gap-3">
                                    {product.tipos_marco.map((frame, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedFrame(frame)}
                                            className={`px-6 py-3 rounded-full border-2 text-sm font-bold uppercase transition-all ${selectedFrame === frame
                                                ? 'border-black bg-black text-white'
                                                : 'border-gray-200 text-gray-600 hover:border-black'
                                                }`}
                                        >
                                            {frame}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Color Selection */}
                        {product?.colores && product.colores.length > 0 && (
                            <div className="mb-12">
                                <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Color</h3>
                                <div className="flex flex-wrap gap-3">
                                    {product.colores.map((color, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setActiveColorIndex(index)}
                                            className={`px-6 py-3 rounded-full border-2 text-sm font-bold uppercase transition-all ${activeColorIndex === index
                                                ? 'border-black bg-black text-white'
                                                : 'border-gray-200 text-gray-600 hover:border-black'
                                                }`}
                                        >
                                            {color}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Size Selection */}
                        <div className="mb-12">
                            <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Choose Size</h3>
                            <div className="grid grid-cols-4 gap-4">
                                {product.sizes && product.sizes.length > 0 ? (
                                    product.sizes.map((variant, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedSize(variant.size)}
                                            className={`h-12 border flex flex-col items-center justify-center transition-all ${selectedSize === variant.size
                                                ? 'border-2 border-black bg-white text-black'
                                                : 'border-gray-200 text-gray-500 hover:border-black'
                                                }`}
                                        >
                                            <span className="text-sm font-bold">{variant.size}</span>
                                        </button>
                                    ))
                                ) : (
                                    // Fallback for objects without variants
                                    ['S', 'M', 'L', 'XL'].map(size => (
                                        <button
                                            key={size}
                                            onClick={() => setSelectedSize(size)}
                                            className={`h-12 border flex items-center justify-center text-sm font-bold transition-all ${selectedSize === size
                                                ? 'border-2 border-black bg-white text-black'
                                                : 'border-gray-200 text-gray-500 hover:border-black'
                                                }`}
                                        >
                                            {size}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Action Links */}
                        {geometry && (
                            <div className="flex space-x-8 mb-10 border-t border-b border-gray-100 py-6">
                                {geometry.hasHeights && (
                                    <button type="button" onClick={() => setSpecView('size')} className="flex items-center text-xs font-bold uppercase tracking-widest hover:text-gray-600">
                                        <Ruler className="w-4 h-4 mr-2" /> Guía de tallas
                                    </button>
                                )}
                                <button type="button" onClick={() => setSpecView('geometry')} className="flex items-center text-xs font-bold uppercase tracking-widest hover:text-gray-600">
                                    <FileText className="w-4 h-4 mr-2" /> Geometría
                                </button>
                            </div>
                        )}
                        {specView && geometry && (
                            <SizeGeometryModal
                                view={specView}
                                onClose={closeSpecView}
                                geometry={geometry}
                                modelName={`Raymon ${product.name}`}
                                availableSizes={(product.sizes || []).map(s => s.size).filter(Boolean)}
                                selectedFrame={selectedFrame}
                            />
                        )}

                        {/* Price & Add to Cart */}
                        <div className="flex items-center justify-between">
                            <div className="text-3xl font-black tracking-tight">
                                {(() => {
                                    const currentVariant = product.sizes?.find(s => s.size === selectedSize);
                                    const displayPrice = currentVariant && currentVariant.price ? currentVariant.price : product.price;
                                    return formatPrice(displayPrice, product.type, product.serie_id);
                                })()}
                            </div>
                            <button
                                onClick={() => {
                                    const currentVariant = product.sizes?.find(s => s.size === selectedSize);
                                    const finalPrice = currentVariant && currentVariant.price ? currentVariant.price : product.price;
                                    addToCart({ ...product, price: finalPrice, selectedSize }, 1);
                                }}
                                className="bg-black text-white px-12 py-4 text-sm font-bold uppercase tracking-widest hover:bg-gray-900 transition-transform transform hover:-translate-y-1"
                            >
                                Add to Cart
                            </button>
                        </div>

                    </div>
                </div>

                {/* Featured Large Image */}
                <div className="mt-32 w-full flex justify-center px-4">
                    <img
                        src={product.images && product.images.length > 0 ? product.images[0] : product.image_url}
                        alt={product.name}
                        className="w-full max-w-7xl h-auto object-contain max-h-[800px] mix-blend-multiply"
                    />
                </div>

                {/* Technical Specifications Section */}
                <div className="mt-20 border-t border-gray-100 pt-20">
                    <h2 className="text-3xl md:text-4xl font-black italic uppercase tracking-tight text-center mb-20 text-black">
                        Especificaciones <span className="gradient-text-dark">Técnicas</span>
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12 max-w-6xl mx-auto">
                        {specsMapping.map((spec, index) => {
                            const value = product[spec.key];
                            if (!value) return null;

                            return (
                                <div key={index} className="flex flex-col border-b border-gray-100 pb-4">
                                    <span className="text-xs font-bold uppercase text-gray-400 tracking-widest mb-2 flex items-center gap-3">
                                        <span className="w-10 h-10 shrink-0 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500">
                                            <spec.icon className="w-6 h-6" aria-hidden="true" />
                                        </span>
                                        {spec.label}
                                    </span>
                                    <span className="font-bold text-lg text-black break-words">
                                        {spec.format ? spec.format(value) : value}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <SpecsNote className="max-w-6xl mx-auto mt-10" />
                </div>

                {/* Related Products Section */}
                {relatedProducts.length > 0 && (
                    <div className="mt-32 border-t border-gray-100 pt-20">
                        <h3 className="text-3xl md:text-4xl font-black italic uppercase tracking-tight text-center mb-16 text-black">
                            También te podría interesar
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            {relatedProducts.map(p => (
                                <Link to={`/product/${slugify(p.modelo)}`} key={p.id} className="group block text-center" onClick={() => window.scrollTo(0, 0)}>
                                    <div className="bg-gray-50 rounded-3xl p-8 mb-6 relative aspect-[4/3] flex items-center justify-center transition-colors group-hover:bg-gray-100">
                                        <img
                                            src={p.imagenes_urls && p.imagenes_urls.length > 0 ? p.imagenes_urls[0] : p.imagen_url}
                                            alt={p.modelo}
                                            className="max-w-full max-h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <h4 className="text-xl font-black uppercase mb-2">{p.modelo}</h4>
                                    <p className="text-gray-500 font-medium text-lg">
                                        {formatPrice(p.precio_eur, 'bikes', p.serie_id)}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

// Specs Definition
const specsMapping = [
    { label: 'Cuadro', key: 'cuadro_material', icon: FrameIcon },
    { label: 'Horquilla', key: 'horquilla', icon: ForkIcon },
    { label: 'Amortiguador', key: 'shock', icon: ShockIcon },
    { label: 'Motor', key: 'motor_modelo', icon: MotorIcon },
    { label: 'Batería', key: 'bateria_wh', format: (val) => `${val} Wh`, icon: BatteryIcon },
    { label: 'Cargador', key: 'charger', icon: ChargerIcon },
    { label: 'Display', key: 'display', icon: DisplayIcon },
    { label: 'Frenos', key: 'frenos_modelo', icon: BrakeIcon },
    { label: 'Discos de Freno', key: 'brake_discs', icon: RotorIcon },
    { label: 'Cambio', key: 'transmision_modelo', icon: DerailleurIcon },
    { label: 'Maneta de Cambio', key: 'shifter', icon: ShifterIcon },
    { label: 'Cassette', key: 'cassette', icon: CassetteIcon },
    { label: 'Cadena', key: 'chain', icon: ChainIcon },
    { label: 'Bielas', key: 'crank_arm', icon: CrankIcon },
    { label: 'Plato', key: 'chainring', icon: CrankIcon },
    { label: 'Ruedas', key: 'wheelset', icon: WheelIcon },
    { label: 'Neumático Delantero', key: 'tire_f', icon: TireIcon },
    { label: 'Neumático Trasero', key: 'tire_r', icon: TireIcon },
    { label: 'Manillar', key: 'handlebar', icon: HandlebarIcon },
    { label: 'Potencia', key: 'stem', icon: StemIcon },
    { label: 'Sillín', key: 'saddle', icon: SaddleIcon },
    { label: 'Tija', key: 'tija_telescopica', format: (val) => (val === true ? 'Telescópica' : val), icon: SeatpostIcon },
    { label: 'Pedales', key: 'pedals', icon: PedalIcon },
    { label: 'Peso', key: 'peso_kg', format: (val) => `${val} kg (${kgToLb(val)} lb)`, icon: WeightIcon },
    { label: 'Peso máximo permitido', key: 'max_weight_kg', format: (val) => `${val} kg (${kgToLb(val)} lb)`, icon: MaxWeightIcon },
];

export default ProductDetail;
